import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  ExternalLink, 
  Plus, 
  MoreHorizontal,
  Building2,
  Globe,
  CheckCircle2,
  X,
  Mail,
  User,
  Hash,
  ChevronRight,
  ChevronLeft,
  Upload,
  CreditCard,
  FileText,
  Settings,
  MapPin,
  Phone,
  DollarSign,
  Calendar,
  Shield,
  Users,
  Check
} from 'lucide-react';

// --- STYLING TOKENS ---
const styles = {
  container: "p-8 max-w-7xl mx-auto animate-in fade-in slide-in-from-bottom-2 duration-500",
  heading: "font-poppins font-semibold text-gray-900 text-2xl mb-1",
  subHeading: "text-sm text-gray-500 mb-8",
  searchWrapper: "relative flex-1 max-w-md",
  searchInput: "w-full pl-10 pr-4 py-1.5 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all",
  card: "bg-white rounded-xl border border-gray-100 p-5 shadow-sm transition-all duration-300 hover:shadow-lg hover:scale-[1.01] group cursor-default",
  statusBadge: "flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100",
  btnView: "flex items-center justify-center gap-2 bg-indigo-600 text-white px-4 py-1.5 rounded-md text-xs font-semibold hover:bg-indigo-700 active:scale-95 transition-all shadow-sm shadow-indigo-200",
  logoPlaceholder: "w-12 h-12 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-100 flex items-center justify-center text-indigo-600 shadow-inner",
  label: "block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5",
  input: "w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:ring-2 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all"
};

export default function Companies() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [companies, setCompanies] = useState([
    { id: 1, name: "GTPL", slug: "gtpl", status: "Active", employees: "1,200", industry: "Telecom", admin: "admin@gtpl.com" },
    { id: 2, name: "Infosys", slug: "infosys", status: "Active", employees: "300,000", industry: "IT Services", admin: "hr@infosys.com" },
    { id: 3, name: "TCS", slug: "tcs", status: "Active", employees: "600,000", industry: "Consulting", admin: "contact@tcs.com" },
  ]);

  // Form State - Multi-step onboarding
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Company Details
    companyName: '',
    companyLegalName: '',
    companyLogo: null,
    industry: 'Technology',
    companyWebsite: '',
    companyEmailDomain: '',
    companyPhone: '',
    country: 'India',
    timeZone: 'Asia/Kolkata',
    defaultLanguage: 'English',
    
    // Step 2: Primary Admin Setup
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPhone: '',
    enablePortalAccess: true,
    
    // Step 3: Subscription, Bundle & Localization
    baseCurrency: 'INR',
    enableMultiCurrency: false,
    dateFormat: 'DD/MM/YYYY',
    numberFormat: '1,000.00',
    
    // Step 4: Company Configuration (Company Code & Employee)
    companyCodePrefix: '',
    orgCode: 'HR',
    customOrgCode: '',
    companyCodeNumber: '001',
    numberPaddingLength: 3,
    hasExistingEmployeeCodes: false,
    permanentEmployeeCode: '',
    temporaryEmployeeCode: 'Not Applicable',
    contractualEmployeeCode: 'Not Applicable',
    chosenEmployeeIdType: 'Numeric',
    
    // Step 5: Customer Master Defaults
    customerType: 'Business',
    customerDisplayName: '',
    customerPrimaryContact: '',
    customerEmail: '',
    customerPhone: '',
    communicationChannels: ['Email'],
    enableCustomerPortal: false,
    
    // Step 6: Tax & Compliance
    gstTreatment: 'Registered Business',
    gstin: '',
    pan: '',
    placeOfSupply: '',
    taxPreference: 'Taxable',
    
    // Step 7: Address
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingPinCode: '',
    billingCountry: 'India',
    shippingSameAsBilling: true,
    shippingAddress: '',
    shippingCity: '',
    shippingState: '',
    shippingPinCode: '',
    shippingCountry: 'India',
    
    // Step 8: Payment Setup
    enableOnlinePayments: true,
    paymentMethods: ['Credit Card', 'Bank Transfer'],
    subscriptionBundle: 'Starter',
    employeeCount: 10,
    baseBundlePrice: 60,
    slabMultiplier: 1.0,
    effectivePrice: 60,
    subtotal: 600,
    gstAmount: 108,
    netPayAmount: 708,
    bankName: '',
    accountNumber: '',
    ifscCode: '',
    accountHolderName: '',
    
    // Step 9: Documents
    companyRegistration: null,
    gstCertificate: null,
    panCard: null,
    bankProof: null,
    otherDocuments: [],
    
    // Step 10: HRMS Defaults
    defaultLeavePolicy: 'Standard',
    workingDays: '5 Days',
    defaultShift: '9 AM - 6 PM',
    customShift: '',
    enablePayroll: true,
    enableRecruitment: true,
    enableTAC: false,
    enableAssetManagement: true
  });

  const totalSteps = 11;
  
  const stepTitles = [
    'Company Details',
    'Primary Admin Setup',
    'Localization',
    'Company Configuration',
    'Customer Master Defaults',
    'Tax & Compliance',
    'Address',
    'Payment Setup',
    'Documents',
    'HRMS Defaults',
    'Final Confirmation'
  ];

  const handleRegister = (e) => {
    e.preventDefault();
    const newCompany = {
      id: companies.length + 1,
      name: formData.companyName,
      slug: formData.companyName.toLowerCase().replace(/\s+/g, '-'),
      status: "Active",
      employees: "0",
      industry: formData.industry,
      admin: formData.adminEmail
    };
    setCompanies([newCompany, ...companies]);
    setIsModalOpen(false);
    setCurrentStep(1);
    // Reset form data
    setFormData({
      companyName: '', companyLegalName: '', companyLogo: null, industry: 'Technology',
      companyWebsite: '', companyEmailDomain: '', companyPhone: '', country: 'India',
      timeZone: 'Asia/Kolkata', defaultLanguage: 'English', adminFirstName: '',
      adminLastName: '', adminEmail: '', adminPhone: '', enablePortalAccess: true,
      baseCurrency: 'INR', enableMultiCurrency: false, dateFormat: 'DD/MM/YYYY',
      numberFormat: '1,000.00', companyCodePrefix: '', orgCode: 'HR',
      customOrgCode: '', companyCodeNumber: '001', numberPaddingLength: 3,
      hasExistingEmployeeCodes: false, permanentEmployeeCode: '',
      temporaryEmployeeCode: 'Not Applicable', contractualEmployeeCode: 'Not Applicable',
      chosenEmployeeIdType: 'Numeric', customerType: 'Business', customerDisplayName: '',
      customerPrimaryContact: '', customerEmail: '', customerPhone: '',
      communicationChannels: ['Email'], enableCustomerPortal: false,
      gstTreatment: 'Registered Business', gstin: '', pan: '', placeOfSupply: '',
      taxPreference: 'Taxable', billingAddress: '', billingCity: '', billingState: '',
      billingPinCode: '', billingCountry: 'India', shippingSameAsBilling: true,
      shippingAddress: '', shippingCity: '', shippingState: '', shippingPinCode: '',
      shippingCountry: 'India', enableOnlinePayments: true,
      paymentMethods: ['Credit Card', 'Bank Transfer'], subscriptionBundle: 'Starter',
      employeeCount: 10, baseBundlePrice: 60, slabMultiplier: 1.0, effectivePrice: 60,
      subtotal: 600, gstAmount: 108, netPayAmount: 708, bankName: '', accountNumber: '',
      ifscCode: '', accountHolderName: '', companyRegistration: null, gstCertificate: null,
      panCard: null, bankProof: null, otherDocuments: [], defaultLeavePolicy: 'Standard',
      workingDays: '5 Days', defaultShift: '9 AM - 6 PM', customShift: '',
      enablePayroll: true, enableRecruitment: true, enableTAC: false, enableAssetManagement: true
    });
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  const updateFormData = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-fill company code prefix when company name changes
      if (field === 'companyName' && value.length >= 4) {
        const prefix = value.substring(0, 4).toUpperCase();
        newData.companyCodePrefix = prefix;
        
        // Auto-increment company code number if similar exists
        const existingCount = companies.filter(c => c.name.substring(0, 4).toUpperCase() === prefix).length;
        if (existingCount > 0) {
          const nextNumber = (existingCount + 1).toString().padStart(newData.numberPaddingLength, '0');
          newData.companyCodeNumber = nextNumber;
        }
      }
      
      // Calculate pricing when employee count or bundle changes
      if (field === 'employeeCount' || field === 'subscriptionBundle') {
        const bundlePrices = { Starter: 60, Basic: 90, Professional: 120, Enterprise: 180 };
        const baseBundlePrice = bundlePrices[newData.subscriptionBundle] || 60;
        
        // Determine slab multiplier
        const employeeCount = newData.employeeCount || 1;
        let slabMultiplier = 1.0;
        if (employeeCount >= 26 && employeeCount <= 50) slabMultiplier = 0.95;
        else if (employeeCount >= 51 && employeeCount <= 100) slabMultiplier = 0.90;
        else if (employeeCount >= 101 && employeeCount <= 250) slabMultiplier = 0.85;
        else if (employeeCount > 250) slabMultiplier = 0.80; // Custom pricing placeholder
        
        const effectivePrice = baseBundlePrice * slabMultiplier;
        const subtotal = employeeCount * effectivePrice;
        
        // GST calculation (only for India and non-overseas)
        let gstAmount = 0;
        if (newData.country === 'India' && newData.gstTreatment !== 'Overseas') {
          gstAmount = Math.round(subtotal * 18 / 100);
        }
        
        const netPayAmount = subtotal + gstAmount;
        
        newData.baseBundlePrice = baseBundlePrice;
        newData.slabMultiplier = slabMultiplier;
        newData.effectivePrice = Math.round(effectivePrice);
        newData.subtotal = Math.round(subtotal);
        newData.gstAmount = gstAmount;
        newData.netPayAmount = Math.round(netPayAmount);
      }
      
      return newData;
    });
  };

  const renderStepContent = () => {
    switch(currentStep) {
      case 1: // Company Details
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Company Name *</label>
                <input className={styles.input} value={formData.companyName} onChange={(e) => updateFormData('companyName', e.target.value)} placeholder="Acme Corp" required />
              </div>
              <div>
                <label className={styles.label}>Legal Name</label>
                <input className={styles.input} value={formData.companyLegalName} onChange={(e) => updateFormData('companyLegalName', e.target.value)} placeholder="Acme Corporation Pvt Ltd" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Industry *</label>
                <select className={styles.input} value={formData.industry} onChange={(e) => updateFormData('industry', e.target.value)}>
                  <option>Technology</option><option>Manufacturing</option><option>Healthcare</option><option>Retail</option><option>Telecom</option><option>Finance</option><option>Education</option>
                </select>
              </div>
              <div>
                <label className={styles.label}>Website</label>
                <input className={styles.input} value={formData.companyWebsite} onChange={(e) => updateFormData('companyWebsite', e.target.value)} placeholder="https://company.com" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Email Domain</label>
                <input className={styles.input} value={formData.companyEmailDomain} onChange={(e) => updateFormData('companyEmailDomain', e.target.value)} placeholder="@company.com" />
              </div>
              <div>
                <label className={styles.label}>Phone Number</label>
                <input className={styles.input} value={formData.companyPhone} onChange={(e) => updateFormData('companyPhone', e.target.value)} placeholder="+91 9876543210" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={styles.label}>Country</label>
                <select className={styles.input} value={formData.country} onChange={(e) => updateFormData('country', e.target.value)}>
                  <option>India</option><option>USA</option><option>UK</option><option>Canada</option><option>Australia</option>
                </select>
              </div>
              <div>
                <label className={styles.label}>Time Zone</label>
                <select className={styles.input} value={formData.timeZone} onChange={(e) => updateFormData('timeZone', e.target.value)}>
                  <option>Asia/Kolkata</option><option>America/New_York</option><option>Europe/London</option><option>Australia/Sydney</option>
                </select>
              </div>
              <div>
                <label className={styles.label}>Language</label>
                <select className={styles.input} value={formData.defaultLanguage} onChange={(e) => updateFormData('defaultLanguage', e.target.value)}>
                  <option>English</option><option>Hindi</option><option>Spanish</option><option>French</option>
                </select>
              </div>
            </div>
            <div>
              <label className={styles.label}>Company Logo</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <Upload className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600">Upload company logo</p>
                <input type="file" className="hidden" accept="image/*" />
              </div>
            </div>
          </div>
        );
      case 2: // Primary Admin Setup
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>First Name *</label>
                <input className={styles.input} value={formData.adminFirstName} onChange={(e) => updateFormData('adminFirstName', e.target.value)} placeholder="John" required />
              </div>
              <div>
                <label className={styles.label}>Last Name *</label>
                <input className={styles.input} value={formData.adminLastName} onChange={(e) => updateFormData('adminLastName', e.target.value)} placeholder="Doe" required />
              </div>
            </div>
            <div>
              <label className={styles.label}>Admin Email *</label>
              <input className={styles.input} type="email" value={formData.adminEmail} onChange={(e) => updateFormData('adminEmail', e.target.value)} placeholder="admin@company.com" required />
            </div>
            <div>
              <label className={styles.label}>Phone Number</label>
              <input className={styles.input} value={formData.adminPhone} onChange={(e) => updateFormData('adminPhone', e.target.value)} placeholder="+91 9876543210" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={formData.enablePortalAccess} onChange={(e) => updateFormData('enablePortalAccess', e.target.checked)} />
              <label className="text-sm text-gray-700">Enable Portal Access</label>
            </div>
          </div>
        );
      case 3: // Localization
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Base Currency *</label>
                <select className={styles.input} value={formData.baseCurrency} onChange={(e) => updateFormData('baseCurrency', e.target.value)}>
                  <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option><option>AUD</option>
                </select>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input type="checkbox" checked={formData.enableMultiCurrency} onChange={(e) => updateFormData('enableMultiCurrency', e.target.checked)} />
                <label className="text-sm text-gray-700">Enable Multi-Currency</label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Date Format</label>
                <select className={styles.input} value={formData.dateFormat} onChange={(e) => updateFormData('dateFormat', e.target.value)}>
                  <option>DD/MM/YYYY</option><option>MM/DD/YYYY</option><option>YYYY-MM-DD</option>
                </select>
              </div>
              <div>
                <label className={styles.label}>Number Format</label>
                <select className={styles.input} value={formData.numberFormat} onChange={(e) => updateFormData('numberFormat', e.target.value)}>
                  <option>1,000.00</option><option>1.000,00</option><option>1 000.00</option>
                </select>
              </div>
            </div>
          </div>
        );
      case 4: // Company Configuration (Company Code & Employee)
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="text-sm font-bold text-blue-800 mb-2">Company Code Preview</h4>
              <p className="text-lg font-mono text-blue-600">
                {formData.companyCodePrefix}-ORG-{formData.companyCodeNumber.padStart(formData.numberPaddingLength, '0')}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Next Code: {formData.companyCodePrefix}-ORG-{(parseInt(formData.companyCodeNumber) + 1).toString().padStart(formData.numberPaddingLength, '0')}
              </p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Company Prefix (Auto-filled)</label>
                <input value={formData.companyCodePrefix} readOnly placeholder="ACME" className={`${styles.input} bg-gray-100`} />
              </div>
              <div>
                <label className={styles.label}>Organization Code</label>
                <input value="ORG" readOnly className={`${styles.input} bg-gray-100 cursor-not-allowed`} />
              </div>
            </div>

            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Starting Number (Auto-incremented)</label>
                <input className={styles.input} value={formData.companyCodeNumber} onChange={(e) => updateFormData('companyCodeNumber', e.target.value)} placeholder="001" />
              </div>
              <div>
                <label className={styles.label}>Number Padding Length</label>
                <select className={styles.input} value={formData.numberPaddingLength} onChange={(e) => updateFormData('numberPaddingLength', parseInt(e.target.value))}>
                  <option value={3}>3 digits (001)</option>
                  <option value={4}>4 digits (0001)</option>
                  <option value={5}>5 digits (00001)</option>
                </select>
              </div>
            </div>

            
            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-800">⚠️ Company code sequence is unique per company to ensure data isolation</p>
            </div>
          </div>
        );
      case 5: // Customer Master Defaults
        return (
          <div className="space-y-4">
            <div>
              <label className={styles.label}>Customer Type</label>
              <div className="flex gap-4">
                {['Business', 'Individual'].map(type => (
                  <button key={type} type="button" onClick={() => updateFormData('customerType', type)} className={`px-4 py-2 rounded-lg text-sm font-medium ${formData.customerType === type ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>{type}</button>
                ))}
              </div>
            </div>
            <div>
              <label className={styles.label}>Display Name</label>
              <input className={styles.input} value={formData.customerDisplayName} onChange={(e) => updateFormData('customerDisplayName', e.target.value)} placeholder="Customer Display Name" />
            </div>
            <div>
              <label className={styles.label}>Primary Contact</label>
              <input className={styles.input} value={formData.customerPrimaryContact} onChange={(e) => updateFormData('customerPrimaryContact', e.target.value)} placeholder="John Doe" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Email</label>
                <input className={styles.input} type="email" value={formData.customerEmail} onChange={(e) => updateFormData('customerEmail', e.target.value)} placeholder="customer@email.com" />
              </div>
              <div>
                <label className={styles.label}>Phone</label>
                <input className={styles.input} value={formData.customerPhone} onChange={(e) => updateFormData('customerPhone', e.target.value)} placeholder="+91 9876543210" />
              </div>
            </div>
            <div>
              <label className={styles.label}>Communication Channels</label>
              <div className="flex gap-4">
                {['Email', 'SMS'].map(channel => (
                  <label key={channel} className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.communicationChannels.includes(channel)} onChange={(e) => {
                      const channels = e.target.checked ? [...formData.communicationChannels, channel] : formData.communicationChannels.filter(c => c !== channel);
                      updateFormData('communicationChannels', channels);
                    }} />
                    <span className="text-sm">{channel}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={formData.enableCustomerPortal} onChange={(e) => updateFormData('enableCustomerPortal', e.target.checked)} />
              <label className="text-sm text-gray-700">Enable Customer Portal</label>
            </div>
          </div>
        );
      case 6: // Tax & Compliance
        return (
          <div className="space-y-4">
            <div>
              <label className={styles.label}>GST Treatment *</label>
              <select className={styles.input} value={formData.gstTreatment} onChange={(e) => updateFormData('gstTreatment', e.target.value)}>
                <option>Registered Business</option><option>Unregistered Business</option><option>Consumer</option><option>Overseas</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>GSTIN</label>
                <input className={styles.input} value={formData.gstin} onChange={(e) => updateFormData('gstin', e.target.value)} placeholder="22AAAAA0000A1Z5" />
              </div>
              <div>
                <label className={styles.label}>PAN</label>
                <input className={styles.input} value={formData.pan} onChange={(e) => updateFormData('pan', e.target.value)} placeholder="AAAAA0000A" />
              </div>
            </div>
            <div>
              <label className={styles.label}>Place of Supply</label>
              <input className={styles.input} value={formData.placeOfSupply} onChange={(e) => updateFormData('placeOfSupply', e.target.value)} placeholder="Gujarat" />
            </div>
            <div>
              <label className={styles.label}>Tax Preference</label>
              <div className="flex gap-4">
                {['Taxable', 'Tax Exempt'].map(pref => (
                  <button key={pref} type="button" onClick={() => updateFormData('taxPreference', pref)} className={`px-4 py-2 rounded-lg text-sm font-medium ${formData.taxPreference === pref ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700'}`}>{pref}</button>
                ))}
              </div>
            </div>
          </div>
        );
      case 7: // Address
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-bold text-gray-800 mb-3">Billing Address</h4>
              <div className="space-y-3">
                <input className={styles.input} value={formData.billingAddress} onChange={(e) => updateFormData('billingAddress', e.target.value)} placeholder="Address Line 1" />
                <div className="grid grid-cols-3 gap-3">
                  <input className={styles.input} value={formData.billingCity} onChange={(e) => updateFormData('billingCity', e.target.value)} placeholder="City" />
                  <input className={styles.input} value={formData.billingState} onChange={(e) => updateFormData('billingState', e.target.value)} placeholder="State" />
                  <input className={styles.input} value={formData.billingPinCode} onChange={(e) => updateFormData('billingPinCode', e.target.value)} placeholder="PIN Code" />
                </div>
                <select className={styles.input} value={formData.billingCountry} onChange={(e) => updateFormData('billingCountry', e.target.value)}>
                  <option>India</option><option>USA</option><option>UK</option><option>Canada</option>
                </select>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={formData.shippingSameAsBilling} onChange={(e) => updateFormData('shippingSameAsBilling', e.target.checked)} />
              <label className="text-sm text-gray-700">Shipping address same as billing</label>
            </div>
            {!formData.shippingSameAsBilling && (
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-3">Shipping Address</h4>
                <div className="space-y-3">
                  <input className={styles.input} value={formData.shippingAddress} onChange={(e) => updateFormData('shippingAddress', e.target.value)} placeholder="Address Line 1" />
                  <div className="grid grid-cols-3 gap-3">
                    <input className={styles.input} value={formData.shippingCity} onChange={(e) => updateFormData('shippingCity', e.target.value)} placeholder="City" />
                    <input className={styles.input} value={formData.shippingState} onChange={(e) => updateFormData('shippingState', e.target.value)} placeholder="State" />
                    <input className={styles.input} value={formData.shippingPinCode} onChange={(e) => updateFormData('shippingPinCode', e.target.value)} placeholder="PIN Code" />
                  </div>
                  <select className={styles.input} value={formData.shippingCountry} onChange={(e) => updateFormData('shippingCountry', e.target.value)}>
                    <option>India</option><option>USA</option><option>UK</option><option>Canada</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        );
      case 8: // Payment Setup
        return (
          <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-bold text-gray-800 mb-3">Subscription Bundle & Pricing</h4>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={styles.label}>Bundle Type *</label>
                  <select className={styles.input} value={formData.subscriptionBundle} onChange={(e) => updateFormData('subscriptionBundle', e.target.value)}>
                    <option value="Starter">Starter - Employee self-service</option>
                    <option value="Basic">Basic - Leave & attendance</option>
                    <option value="Professional">Professional - HR workflows & reports</option>
                    <option value="Enterprise">Enterprise - Payroll, TAC, automation</option>
                  </select>
                </div>
                <div>
                  <label className={styles.label}>Number of Employees</label>
                  <input type="number" value={formData.employeeCount} onChange={(e) => updateFormData('employeeCount', parseInt(e.target.value))} placeholder="10" min="1" className={styles.input} />
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg border">
                <h5 className="text-sm font-bold text-gray-700 mb-3">Pricing Calculation</h5>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Selected Bundle:</span>
                    <span className="font-medium">{formData.subscriptionBundle}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Base Price per Employee:</span>
                    <span className="font-medium">₹{formData.baseBundlePrice}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Employee Count:</span>
                    <span className="font-medium">{formData.employeeCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Slab Multiplier ({formData.employeeCount <= 25 ? '1-25' : formData.employeeCount <= 50 ? '26-50' : formData.employeeCount <= 100 ? '51-100' : formData.employeeCount <= 250 ? '101-250' : '250+'}):</span>
                    <span className="font-medium">{formData.slabMultiplier}x</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Effective Price per Employee:</span>
                    <span className="font-medium">₹{formData.effectivePrice}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span>Subtotal:</span>
                    <span className="font-medium">₹{formData.subtotal}</span>
                  </div>
                  {formData.gstAmount > 0 && (
                    <div className="flex justify-between">
                      <span>GST (18%):</span>
                      <span className="font-medium">₹{formData.gstAmount}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t pt-2 font-bold text-lg">
                    <span>Net Payable Amount:</span>
                    <span className="text-green-600">₹{formData.netPayAmount}</span>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-yellow-50 rounded text-xs text-yellow-800">
                  ⚠️ This is an estimated price. Final billing may vary based on verification and local tax rules.
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={formData.enableOnlinePayments} onChange={(e) => updateFormData('enableOnlinePayments', e.target.checked)} />
              <label className="text-sm font-bold text-gray-700">Enable Online Payments</label>
            </div>
            <div>
              <label className={styles.label}>Payment Methods</label>
              <div className="grid grid-cols-2 gap-2">
                {['Credit Card', 'Debit Card', 'UPI', 'Bank Transfer'].map(method => (
                  <label key={method} className="flex items-center gap-2">
                    <input type="checkbox" checked={formData.paymentMethods.includes(method)} onChange={(e) => {
                      const methods = e.target.checked ? [...formData.paymentMethods, method] : formData.paymentMethods.filter(m => m !== method);
                      updateFormData('paymentMethods', methods);
                    }} />
                    <span className="text-sm">{method}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <h4 className="text-sm font-bold text-gray-800">Bank Details</h4>
              <div className="grid grid-cols-2 gap-3">
                <input className={styles.input} value={formData.bankName} onChange={(e) => updateFormData('bankName', e.target.value)} placeholder="Bank Name" />
                <input className={styles.input} value={formData.accountNumber} onChange={(e) => updateFormData('accountNumber', e.target.value)} placeholder="Account Number" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className={styles.input} value={formData.ifscCode} onChange={(e) => updateFormData('ifscCode', e.target.value)} placeholder="IFSC Code" />
                <input className={styles.input} value={formData.accountHolderName} onChange={(e) => updateFormData('accountHolderName', e.target.value)} placeholder="Account Holder" />
              </div>
            </div>
          </div>
        );
      case 9: // Documents
        return (
          <div className="space-y-4">
            <div className="text-center p-8 border-2 border-dashed border-gray-300 rounded-lg">
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-sm text-gray-600">Upload company documents</p>
              <p className="text-xs text-gray-500 mt-1">Max 10MB per file • PDF, JPG, PNG supported</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Company Registration</label>
                <input type="file" className={styles.input} accept=".pdf,.jpg,.png" />
              </div>
              <div>
                <label className={styles.label}>GST Certificate</label>
                <input type="file" className={styles.input} accept=".pdf,.jpg,.png" />
              </div>
              <div>
                <label className={styles.label}>PAN Card</label>
                <input type="file" className={styles.input} accept=".pdf,.jpg,.png" />
              </div>
              <div>
                <label className={styles.label}>Bank Proof</label>
                <input type="file" className={styles.input} accept=".pdf,.jpg,.png" />
              </div>
            </div>
            <div>
              <label className={styles.label}>Other Documents (Multi-upload)</label>
              <input type="file" className={styles.input} accept=".pdf,.jpg,.png" multiple />
            </div>
          </div>
        );
      case 10: // HRMS Defaults
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={styles.label}>Default Leave Policy</label>
                <select className={styles.input} value={formData.defaultLeavePolicy} onChange={(e) => updateFormData('defaultLeavePolicy', e.target.value)}>
                  <option>Standard</option><option>Flexible</option><option>Custom</option>
                </select>
              </div>
              <div>
                <label className={styles.label}>Working Days</label>
                <select className={styles.input} value={formData.workingDays} onChange={(e) => updateFormData('workingDays', e.target.value)}>
                  <option>5 Days</option><option>6 Days</option><option>Custom</option>
                </select>
              </div>
            </div>
            <div>
              <label className={styles.label}>Default Shift</label>
              <select className={styles.input} value={formData.defaultShift} onChange={(e) => updateFormData('defaultShift', e.target.value)}>
                <option>9 AM - 6 PM</option><option>10 AM - 7 PM</option><option>Flexible</option><option>Other</option>
              </select>
              {formData.defaultShift === 'Other' && (
                <div className="mt-2">
                  <input className={styles.input} value={formData.customShift} onChange={(e) => updateFormData('customShift', e.target.value)} placeholder="Enter custom shift timing" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-bold text-gray-800">Enable Modules</h4>
              {[
                {key: 'enablePayroll', label: 'Payroll Module'},
                {key: 'enableRecruitment', label: 'Recruitment Module'},
                {key: 'enableTAC', label: 'TAC/Recruitment Team'},
                {key: 'enableAssetManagement', label: 'Asset Management'}
              ].map(module => (
                <label key={module.key} className="flex items-center gap-2">
                  <input type="checkbox" checked={formData[module.key]} onChange={(e) => updateFormData(module.key, e.target.checked)} />
                  <span className="text-sm">{module.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      case 11: // Final Confirmation
        return (
          <div className="space-y-6">
            <div className="text-center">
              <CheckCircle2 className="mx-auto h-16 w-16 text-green-500 mb-4" />
              <h3 className="text-lg font-bold text-gray-900 mb-2">Ready to Create Company Workspace</h3>
              <p className="text-sm text-gray-600">Review your configuration and complete the onboarding process.</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Company:</span>
                <span className="font-medium">{formData.companyName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Admin:</span>
                <span className="font-medium">{formData.adminEmail}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Company Code:</span>
                <span className="font-medium">{formData.companyCodePrefix}-ORG-{formData.companyCodeNumber.padStart(formData.numberPaddingLength, '0')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Bundle:</span>
                <span className="font-medium">{formData.subscriptionBundle}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Employees:</span>
                <span className="font-medium">{formData.employeeCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Net Amount:</span>
                <span className="font-medium">₹{formData.netPayAmount}</span>
              </div>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="text-sm font-bold text-blue-800 mb-2">🔐 Data Isolation Rules</h4>
              <ul className="text-xs text-blue-700 space-y-1">
                <li>• Each company has completely isolated data</li>
                <li>• Invoice numbers are unique per company</li>
                <li>• Company configuration is separate from invoice settings</li>
                <li>• Currency & payment methods are company-specific</li>
                <li>• All configurations can be modified later</li>
              </ul>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const filteredCompanies = companies.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className={styles.container}>
      {/* HEADER SECTION */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className={styles.heading}>Company Directory</h1>
          <p className={styles.subHeading}>Manage and monitor registered enterprise entities.</p>
        </div>
        <button 
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2.5 rounded-lg text-xs font-bold hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 active:scale-95"
        >
          <Plus size={16} strokeWidth={3} /> Register New Company
        </button>
      </div>

      {/* FILTERS & SEARCH */}
      <div className="flex items-center gap-4 mb-6">
        <div className={styles.searchWrapper}>
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <input 
            type="text" 
            placeholder="Search by name or slug..." 
            className={styles.searchInput}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <button className="flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 transition-colors">
          <Filter size={14} /> Filter
        </button>
      </div>

      {/* COMPANY GRID */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCompanies.map((company) => (
          <div key={company.id} className={styles.card}>
            <div className="flex justify-between items-start mb-4">
              <div className={styles.logoPlaceholder}>
                <Building2 size={24} strokeWidth={1.5} />
              </div>
              <div className={styles.statusBadge}>
                <CheckCircle2 size={10} />
                {company.status}
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-poppins font-bold text-gray-900 text-lg group-hover:text-indigo-600 transition-colors">
                {company.name}
              </h3>
              <div className="flex items-center gap-2 mt-1 text-gray-400">
                <Globe size={12} />
                <span className="text-xs font-medium">app.hrms.com/{company.slug}</span>
              </div>
              <div className="flex items-center gap-2 mt-1 text-gray-400">
                <Mail size={12} />
                <span className="text-[11px] truncate">{company.admin}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 py-4 border-y border-gray-50 mb-6">
              <div>
                <p className="text-[10px] uppercase font-bold text-gray-400 tracking-tight">Industry</p>
                <p className="text-xs font-semibold text-gray-700">{company.industry}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-bold text-gray-400 tracking-tight">Employees</p>
                <p className="text-xs font-semibold text-gray-700">{company.employees}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate(`/global-admin/companies/${company.slug}`)}
                className={styles.btnView}
              >
                View Details <ExternalLink size={12} />
              </button>
              <button className="p-2 text-gray-400 hover:bg-gray-50 rounded-md transition-colors ml-auto">
                <MoreHorizontal size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* MULTI-STEP ONBOARDING MODAL */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm transition-opacity">
          <div className="w-full max-w-4xl max-h-[90vh] bg-white shadow-2xl rounded-2xl animate-in fade-in zoom-in-95 duration-300 flex flex-col">
            {/* Header with Progress */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Company Onboarding</h2>
                  <p className="text-xs text-gray-500">Step {currentStep} of {totalSteps}: {stepTitles[currentStep - 1]}</p>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                  <X size={20} className="text-gray-400" />
                </button>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-indigo-600 h-2 rounded-full transition-all duration-300" style={{width: `${(currentStep / totalSteps) * 100}%`}}></div>
              </div>
              
              {/* Step Indicators */}
              <div className="flex justify-between mt-4 text-xs">
                {stepTitles.map((title, index) => (
                  <div key={index} className={`flex flex-col items-center ${index + 1 <= currentStep ? 'text-indigo-600' : 'text-gray-400'}`}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mb-1 ${
                      index + 1 < currentStep ? 'bg-indigo-600 text-white' : 
                      index + 1 === currentStep ? 'bg-indigo-100 text-indigo-600 border-2 border-indigo-600' : 
                      'bg-gray-200 text-gray-500'
                    }`}>
                      {index + 1 < currentStep ? <Check size={12} /> : index + 1}
                    </div>
                    <span className="text-center leading-tight" style={{fontSize: '9px', maxWidth: '60px'}}>{title}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2">
                  {currentStep === 1 && <Building2 size={20} className="text-indigo-600" />}
                  {currentStep === 2 && <User size={20} className="text-indigo-600" />}
                  {currentStep === 3 && <DollarSign size={20} className="text-indigo-600" />}
                  {currentStep === 4 && <FileText size={20} className="text-indigo-600" />}
                  {currentStep === 5 && <Users size={20} className="text-indigo-600" />}
                  {currentStep === 6 && <Shield size={20} className="text-indigo-600" />}
                  {currentStep === 7 && <MapPin size={20} className="text-indigo-600" />}
                  {currentStep === 8 && <CreditCard size={20} className="text-indigo-600" />}
                  {currentStep === 9 && <Upload size={20} className="text-indigo-600" />}
                  {currentStep === 10 && <Settings size={20} className="text-indigo-600" />}
                  {currentStep === 11 && <CheckCircle2 size={20} className="text-green-600" />}
                  {stepTitles[currentStep - 1]}
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  {currentStep === 1 && "Enter your company's basic information, logo, and contact details."}
                  {currentStep === 2 && "Set up the primary administrator who will manage this company."}
                  {currentStep === 3 && "Configure currency, date formats, and localization preferences."}
                  {currentStep === 4 && "Set up unique company code and employee code configuration."}
                  {currentStep === 5 && "Configure default customer master setup for invoicing."}
                  {currentStep === 6 && "Enter tax registration and compliance information."}
                  {currentStep === 7 && "Provide billing and shipping address information."}
                  {currentStep === 8 && "Choose subscription bundle and configure payment methods."}
                  {currentStep === 9 && "Upload required company documents and certificates."}
                  {currentStep === 10 && "Set default HRMS policies and enable required modules."}
                  {currentStep === 11 && "Review all information and complete the onboarding process."}
                </p>
              </div>
              
              {renderStepContent()}
            </div>

            {/* Navigation Footer */}
            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-between rounded-b-2xl">
              <button 
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${
                  currentStep === 1 
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              
              <div className="flex gap-3">
                {currentStep < totalSteps ? (
                  <button 
                    type="button"
                    onClick={nextStep}
                    className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 shadow-lg shadow-indigo-100 transition-all"
                  >
                    Next Step <ChevronRight size={16} />
                  </button>
                ) : (
                  <>
                    <button 
                      type="button"
                      className="px-4 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm font-bold hover:bg-white transition-all"
                    >
                      Save as Draft
                    </button>
                    <button 
                      onClick={handleRegister}
                      className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg text-sm font-bold hover:bg-green-700 shadow-lg shadow-green-100 transition-all"
                    >
                      <CheckCircle2 size={16} /> Complete Onboarding
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EMPTY STATE */}
      {filteredCompanies.length === 0 && (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-gray-200">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mb-4">
            <Building2 size={32} />
          </div>
          <p className="text-gray-500 font-medium">No companies found matching "{searchTerm}"</p>
          <button 
            onClick={() => setSearchTerm("")}
            className="text-indigo-600 text-xs font-bold mt-2 hover:underline"
          >
            Clear search results
          </button>
        </div>
      )}
    </div>
  );
}

